/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithRefresh } from 'jotai/utils';
import { loadable } from '../utils/loadable';
import type { IncidentConfig } from '../types';

const INCIDENT_CONFIG_URL = '/api/incident-config';

export async function fetchIncidentConfig(): Promise<IncidentConfig[]> {
  const response = await fetch(INCIDENT_CONFIG_URL, { credentials: 'include' });
  if (!response.ok) {
    throw new Error(await failureMessage(response));
  }
  const document: { incidents: IncidentConfig[] } = await response.json();
  return document.incidents;
}

// The API says why the list could not be served -- a validation failure names
// the offending entry, which is what the person who just uploaded the file
// needs to read. Anything answering without a JSON body (a load balancer
// error page, say) is reported by status alone.
async function failureMessage(response: Response): Promise<string> {
  if (response.headers.get('content-type')?.includes('application/json')) {
    const body: { message?: unknown } = await response.json();
    if (typeof body.message === 'string') return body.message;
  }
  return `Incident config request failed: ${response.status}`;
}

/**
 * The incident page list, requested once per session. Writing to the atom
 * requests it again -- the retry behind the dashboard's error alert.
 */
export const incidentConfigAtom = atomWithRefresh(() => fetchIncidentConfig());

/**
 * One incident's entry, or undefined while the list is loading, if it failed
 * to load, or if the incident is not listed. Never suspends, so the header and
 * the map title can fall back to the id in the URL without holding up the
 * rest of the shell.
 */
export const incidentConfigByIdAtom = atomFamily((incidentId: string | undefined) =>
  atom(get => {
    const config = get(loadable(incidentConfigAtom));
    return config.state === 'hasData' ? config.data.find(i => i.id === incidentId) : undefined;
  })
);

/**
 * One incident's entry once the list request has settled, undefined if it
 * failed or does not list the incident. Suspends until then and never rejects:
 * a broken config must not take an incident page down with it (IncidentConfigAlert
 * reports the failure on the dashboard and in the incident sidebar), while the
 * model picker and the stats must not query before the disabled models are
 * known, or they would query twice.
 */
export const settledIncidentConfigByIdAtom = atomFamily((incidentId: string | undefined) =>
  atom(async get => {
    try {
      return (await get(incidentConfigAtom)).find(i => i.id === incidentId);
    } catch {
      return undefined;
    }
  })
);

/** The POST runs hidden for a section: the incident-wide list plus the section's own. */
export function disabledModelsFor(
  incident: IncidentConfig | undefined,
  sectionId: string
): string[] {
  return [
    ...(incident?.globalSettings?.disabledModels ?? []),
    ...(incident?.sectionSettings?.[sectionId]?.disabledModels ?? []),
  ];
}
